import React, { useState, useEffect } from 'react';
import {
  Card,
  DatePicker,
  Button,
  Table,
  Tabs,
  Space,
  Typography,
  message,
  Spin,
  Tag,
  Row,
  Col,
  Statistic
} from 'antd';
import {
  TrophyOutlined,
  ShopOutlined,
  ReloadOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getTopProductsByLocation, getTopProductsAllLocations } from '../../../actions/report';
const { Title, Text } = Typography;
const { TabPane } = Tabs;

const TopSellingProducts = () => {
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [byLocationData, setByLocationData] = useState(null);
  const [allLocationsData, setAllLocationsData] = useState(null);
  const [activeTab, setActiveTab] = useState('1');

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const year = selectedDate.year();
      const month = selectedDate.month() + 1; // dayjs months are 0-indexed

      // Fetch both endpoints
      const [locationData, allLocationData] = await Promise.all([
        getTopProductsByLocation(year, month),
        getTopProductsAllLocations(year, month)
      ]);

      setByLocationData(locationData);
      setAllLocationsData(allLocationData);
      message.success('Data loaded successfully');
    } catch (error) {
      message.error(error.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const getRankColor = (rank) => {
    switch (rank) {
      case 1: return 'gold';
      case 2: return 'silver';
      case 3: return '#cd7f32'; // bronze
      default: return 'default';
    }
  };

  const getRankIcon = (rank) => {
    return <TrophyOutlined style={{ color: getRankColor(rank), fontSize: '18px' }} />;
  };

  // Columns for location-wise table
  const locationColumns = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      key: 'rank',
      width: 80,
    },
    {
      title: 'Product Title',
      dataIndex: 'product_title',
      key: 'product_title',
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Product Code',
      dataIndex: 'product_code',
      key: 'product_code',
    },
    {
      title: 'Quantity Sold',
      dataIndex: 'total_quantity_sold',
      key: 'total_quantity_sold',
    //   render: (value) => (
    //     <Tag color="blue" style={{ fontSize: '14px' }}>
    //       {value} units
    //     </Tag>
    //   ),
    },
  ];

  // Columns for all locations table
  const allLocationsColumns = [
    {
      title: 'Rank',
      dataIndex: 'rank',
      key: 'rank',
      width: 80,

    },
    {
      title: 'Product Title',
      dataIndex: 'product_title',
      key: 'product_title',
      render: (text) => <Text strong style={{ fontSize: '15px' }}>{text}</Text>,
    },
    {
      title: 'Product Code',
      dataIndex: 'product_code',
      key: 'product_code',
    },
    {
      title: 'Total Quantity Sold',
      dataIndex: 'total_quantity_sold',
      key: 'total_quantity_sold',
    },
  ];

  const getLocations = () => {
    if (byLocationData && byLocationData.locations) {
      return byLocationData.locations;
    }
    return [];
  };

  const getTopProducts = () => {
    if (allLocationsData && allLocationsData.top_products) {
      return allLocationsData.top_products;
    }
    return [];
  };

  const getLocationsCount = () => {
    if (byLocationData && byLocationData.locations) {
      return byLocationData.locations.length;
    }
    return 0;
  };

  return (
    <div style={{ padding: '24px', background: '#f0f2f5', minHeight: '100vh' }}>
      <Card>
        <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
          <Col>
            <Title level={2}>
              <BarChartOutlined /> Top Selling Products
            </Title>
          </Col>
        </Row>

        <Space size="large" style={{ marginBottom: '24px' }}>
          <DatePicker
            picker="month"
            value={selectedDate}
            onChange={handleDateChange}
            format="MMMM YYYY"
            style={{ width: 200 }}
            allowClear={false}
          />
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={fetchData}
            loading={loading}
          >
            Load Data
          </Button>
        </Space>

        <Spin spinning={loading}>
        {(byLocationData || allLocationsData) && (
        <Card style={{ marginTop: '16px', background: '#fafafa' }}>
            <Row gutter={16}>
            <Col span={12}>
                <Statistic
                title="Report Month"
                value={selectedDate.format('MMMM YYYY')}
                prefix={<BarChartOutlined />}
                />
            </Col>
            <Col span={12}>
                <Statistic
                title="Total Locations"
                value={getLocationsCount()}
                prefix={<ShopOutlined />}
                />
            </Col>
            </Row>
        </Card>
        )}
          <Tabs activeKey={activeTab} onChange={setActiveTab}>
            <TabPane
              tab={
                <span>
                  <ShopOutlined />
                  By Location
                </span>
              }
              key="1"
            >
              {getLocations().map((location) => (
                <Card
                  key={location.location_id}
                  style={{ marginBottom: '16px' }}
                  title={
                    <Space>
                      <ShopOutlined style={{ color: '#1890ff' }} />
                      <Text strong style={{ fontSize: '16px' }}>
                        {location.location_name}
                      </Text>
                    </Space>
                  }
                //   extra={
                //     <Tag color="green">
                //       {location.top_products.length} Products
                //     </Tag>
                //   }
                >
                  <Table
                    columns={locationColumns}
                    dataSource={location.top_products}
                    pagination={false}
                    rowKey={(record) => `${record.location_id}-${record.product_id}`}
                    size="middle"
                  />
                </Card>
              ))}

              {getLocations().length === 0 && (
                <Card>
                  <Text type="secondary">No data available for the selected month</Text>
                </Card>
              )}
            </TabPane>

            <TabPane
              tab={
                <span>
                  <TrophyOutlined />
                  All Locations
                </span>
              }
              key="2"
            >
              <Card
                title={
                  <Space>
                    <Text strong style={{ fontSize: '16px' }}>
                      Top 3 Products Across All Locations
                    </Text>
                  </Space>
                }
              >
                <Table
                  columns={allLocationsColumns}
                  dataSource={getTopProducts()}
                  pagination={false}
                  rowKey={(record) => record.product_id}
                  size="large"
                />

                {getTopProducts().length === 0 && (
                  <Text type="secondary">No data available for the selected month</Text>
                )}
              </Card>
            </TabPane>
          </Tabs>
        </Spin>

        
      </Card>
    </div>
  );
};

export default TopSellingProducts;